import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
  discardProductDraft,
  renewProductDraft
} from '../../../../actions/product';

import {
  Box, Button, Chip, Grid, LinearProgress,
  Stack, Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

const mexicoDate = value => value
  ? new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City'
  }).format(new Date(value))
  : 'Sin fecha';

const remainingDays = value => Math.max(
  0,
  Math.ceil((new Date(value).getTime() - Date.now()) / 86400000)
);

const imageCount = draft => (draft.additional_images?.length || 0)
  + (draft.variants || []).reduce(
    (total, variant) => total + (variant.images?.length || 0),
    0
  );

const ProductDraftList = ({ drafts }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [savingId, setSavingId] = useState('');

  const renew = async draftId => {
    setSavingId(draftId);
    try {
      await dispatch(renewProductDraft(draftId));
    } finally {
      setSavingId('');
    }
  };

  const discard = draft => {
    Swal.fire({
      title: '¿Descartar este borrador?',
      text: 'Se eliminarán sus imágenes y su carpeta de Drive.',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'Descartar',
      denyButtonText: 'Cancelar'
    }).then(async result => {
      if (!result.isConfirmed)
        return;
      setSavingId(draft._id);
      try {
        await dispatch(discardProductDraft(draft._id));
      } finally {
        setSavingId('');
      }
    });
  };

  if (drafts.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 2 }}>
      No hay borradores que coincidan con la búsqueda.
    </Typography>;
  }

  return <Grid container spacing={2}>
    {drafts.map(draft => {
      const days = remainingDays(draft.expires_at);
      const warning = days <= 7;
      const saving = savingId === draft._id;

      return <Grid item key={draft._id} xs={12} md={6} xl={4}>
        <Box sx={{
          backgroundColor: warning ? '#fff8e1' : '#fff',
          border: `1px solid ${warning ? '#edb21f' : '#ddd'}`,
          borderRadius: 2,
          height: '100%',
          overflow: 'hidden',
          p: 2
        }}>
          {saving && <LinearProgress sx={{ mb: 1 }} />}
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography fontWeight={600}>
              {draft.title?.trim() || 'Producto sin nombre'}
            </Typography>
            <Chip
              color={warning ? 'warning' : 'default'}
              label={`${days} días restantes`}
              size="small"
            />
          </Stack>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {`${draft.variants?.length || 0} variantes · ${imageCount(draft)} imágenes`}
          </Typography>
          <Typography color="text.secondary" variant="caption" display="block">
            {`Creado por ${draft.created_by?.name || 'Administrador'} · ${mexicoDate(draft.created_at)}`}
          </Typography>
          <Typography color="text.secondary" variant="caption" display="block">
            {`Última actualización: ${mexicoDate(draft.updated_at)}`}
          </Typography>
          {draft.cleanup_status === 'cleanup_error' && <Chip
            color="error"
            label="Limpieza pendiente"
            size="small"
            sx={{ mt: 1 }}
          />}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <Button
              disabled={saving}
              onClick={() => navigate(
                `/nefro-admin/producto-agregar?draft_id=${draft._id}`
              )}
              startIcon={<EditIcon />}
              variant="contained"
            >
              Continuar
            </Button>
            <Button
              disabled={saving}
              onClick={() => renew(draft._id)}
              startIcon={<EventRepeatIcon />}
              variant="outlined"
            >
              Renovar vigencia
            </Button>
            <Button
              color="error"
              disabled={saving}
              onClick={() => discard(draft)}
              startIcon={<DeleteOutlineIcon />}
            >
              Descartar
            </Button>
          </Stack>
        </Box>
      </Grid>;
    })}
  </Grid>;
};

export default ProductDraftList;
